import React from 'react';

const SIGN_ORDER = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const PLANET_LABELS = {
  sun: 'Sun',
  moon: 'Moon',
  mercury: 'Mercury',
  venus: 'Venus',
  mars: 'Mars',
  jupiter: 'Jupiter',
  saturn: 'Saturn',
  uranus: 'Uranus',
  neptune: 'Neptune',
  pluto: 'Pluto',
  northnode: 'North Node',
  lilith: 'Lilith',
  chiron: 'Chiron',
};

function ordinalHouse(value) {
  const house = Number(value);
  if (house === 1) return '1st';
  if (house === 2) return '2nd';
  if (house === 3) return '3rd';
  return `${house}th`;
}

function getPlanetLabel(key) {
  return PLANET_LABELS[String(key).toLowerCase()] || String(key);
}

function computeStructure(planets) {
  if (!planets?.length) return { signEmphasis: null, houseEmphasis: null, polarityAxes: [] };

  const signCounts = {};
  const houseCounts = {};
  const signPlanets = {};
  const housePlanets = {};

  planets.forEach((p) => {
    if (p.sign) {
      signCounts[p.sign] = (signCounts[p.sign] || 0) + 1;
      if (!signPlanets[p.sign]) signPlanets[p.sign] = [];
      signPlanets[p.sign].push(p);
    }
    const h = Number(p.house);
    if (Number.isFinite(h)) {
      houseCounts[h] = (houseCounts[h] || 0) + 1;
      if (!housePlanets[h]) housePlanets[h] = [];
      housePlanets[h].push(p);
    }
  });

  const signEmphasis = Object.entries(signCounts)
    .filter(([, c]) => c >= 3)
    .sort((a, b) => b[1] - a[1])[0];

  const houseEmphasis = Object.entries(houseCounts)
    .filter(([, c]) => c >= 3)
    .sort((a, b) => b[1] - a[1])[0];

  const polarityAxes = [];
  for (let i = 0; i < 6; i++) {
    const signA = SIGN_ORDER[i];
    const signB = SIGN_ORDER[i + 6];
    const countA = signCounts[signA] || 0;
    const countB = signCounts[signB] || 0;
    if (countA > 0 && countB > 0) {
      polarityAxes.push({ signA, signB });
    }
  }

  return {
    signEmphasis: signEmphasis ? { sign: signEmphasis[0], count: signEmphasis[1] } : null,
    houseEmphasis: houseEmphasis
      ? {
          house: Number(houseEmphasis[0]),
          count: houseEmphasis[1],
          planets: (housePlanets[houseEmphasis[0]] || []).map((p) => getPlanetLabel(p.key)),
        }
      : null,
    polarityAxes,
  };
}

const ChartStructurePanel = ({ chart, onHoverSector }) => {
  const structure = React.useMemo(() => computeStructure(chart?.planets), [chart?.planets]);
  const hasContent = structure.signEmphasis || structure.houseEmphasis || structure.polarityAxes.length > 0;

  if (!chart || !hasContent) return null;

  return (
    <section className="card chart-structure-panel">
      <h3>Chart structure</h3>
      <p className="chart-structure-intro">Where the chart concentrates energy.</p>
      <div className="chart-structure-grid">
        {structure.signEmphasis && (
          <div
            className="chart-structure-item"
            onMouseEnter={() => onHoverSector?.('sign', structure.signEmphasis.sign)}
            onMouseLeave={() => onHoverSector?.()}
          >
            <span className="chart-structure-label">{structure.signEmphasis.sign} emphasis</span>
            <span className="chart-structure-value">{structure.signEmphasis.count} placements</span>
          </div>
        )}
        {structure.houseEmphasis && (
          <div
            className="chart-structure-item"
            onMouseEnter={() => onHoverSector?.('house', structure.houseEmphasis.house)}
            onMouseLeave={() => onHoverSector?.()}
          >
            <span className="chart-structure-label">{ordinalHouse(structure.houseEmphasis.house)} House emphasis</span>
            <span className="chart-structure-value">{structure.houseEmphasis.planets.join(' • ')}</span>
          </div>
        )}
        {structure.polarityAxes.map(({ signA, signB }) => (
          <div key={`${signA}-${signB}`} className="chart-structure-item chart-structure-axis">
            <span className="chart-structure-value">{signA} ↔ {signB} axis active</span>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ChartStructurePanel;
